import React, { useEffect } from 'react';
import { isMusic24Locale } from 'fym-shared/src/music24/music24Locales';
import { TypedMemo } from '../../common/types';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { useUser } from '../../users/hooks/useUser';
import { useAppLocale } from './AppIntlProvider';
import { readStoredAppLocale, readStoredAppLocaleSource } from './appLocaleStorage';

/**
 * Keeps the UI language in step with the session:
 * - signed out: a language that a visitor picked (music24.com, the sign-in page) stays, so the
 *   sign-in page opens in it (QA 2026-10-28, lit-1). A language of the last account goes (sign-out).
 * - signed in: the language the user saved (`GET /users/:id`, `user.music24Locale`) wins over the
 *   stored one when it arrives. A user who saved no language ("Automatic") keeps a language that
 *   was picked as a visitor just before; the welcome page saves it to the new account
 *   (`useSaveVisitorLocale`). An older server sends no field; then the stored choice stays.
 *
 * This component sits outside the tRPC provider, so it writes nothing to the server.
 */
const UserLocaleSyncComponent: React.FC = () => {
  const userToken = useUserToken();
  const { data } = useUser();
  const { setPreference } = useAppLocale();
  const signedIn = Boolean(userToken);
  const serverLocale = data?.user.music24Locale;

  useEffect(() => {
    if (!signedIn && readStoredAppLocaleSource() === 'account') {
      setPreference(null);
    }
  }, [setPreference, signedIn]);

  useEffect(() => {
    if (!signedIn || serverLocale === undefined) {
      return;
    }
    if (isMusic24Locale(serverLocale)) {
      setPreference(serverLocale, 'account');
      return;
    }
    const hasVisitorPick = readStoredAppLocaleSource() === 'visitor' && readStoredAppLocale() !== null;
    if (!hasVisitorPick) {
      setPreference(null);
    }
  }, [serverLocale, setPreference, signedIn]);

  return null;
};

export const UserLocaleSync = TypedMemo(UserLocaleSyncComponent);
